/**
 * TournamentEngine - Pure business logic for tournament bracket and pool generation
 * 
 * NO UI DEPENDENCIES - NO DATABASE CALLS - 100% TESTABLE
 * 
 * Features:
 * - Single elimination brackets
 * - Double elimination brackets
 * - Round robin pool play
 * - Automatic winner advancement
 * - Pool standings calculation
 * - Snake draft seeding
 */

type Team = {
  id: string;
  name: string;
  seed?: number;
};

type Match = {
  id: string;
  round: number;
  match_number: number;
  team1_id: string | null;
  team2_id: string | null;
  team1_score: number | null;
  team2_score: number | null;
  winner_id: string | null;
  next_match_id: string | null; // For bracket advancement
  status: 'pending' | 'in_progress' | 'completed';
  pool_id?: string | null;
};

type Pool = {
  id: string;
  name: string;
  teams: Team[];
};

type BracketType = 'single_elimination' | 'double_elimination';

export class TournamentEngine {
  
  // ============================================
  // BRACKET GENERATION
  // ============================================

  /**
   * Generate single elimination bracket
   * Returns array of matches with advancement logic
   * 
   * Example: 8 teams = 7 matches (4 + 2 + 1)
   * Handles odd team counts with automatic byes
   */
  static generateSingleEliminationBracket(teams: Team[]): Match[] {
    const matches: Match[] = [];
    const teamCount = teams.length;
    
    // Calculate total rounds needed (log2 of next power of 2)
    const totalRounds = Math.ceil(Math.log2(teamCount));
    
    // Sort teams by seed for fair matchups
    const sortedTeams = [...teams].sort((a, b) => (a.seed || 999) - (b.seed || 999));
    
    // Calculate first round size (power of 2)
    const firstRoundSize = Math.pow(2, totalRounds);
    const byes = firstRoundSize - teamCount;
    
    let matchCounter = 1;
    let currentRound = 1;
    
    // Generate first round matches with seeded matchups
    const firstRoundMatches: Match[] = [];
    let teamIndex = 0;
    
    for (let i = 0; i < firstRoundSize / 2; i++) {
      const team1 = teamIndex < sortedTeams.length ? sortedTeams[teamIndex++] : null;
      const team2 = teamIndex < sortedTeams.length ? sortedTeams[teamIndex++] : null;
      
      // If one team is null, other team gets a bye (automatic win)
      if (!team2 && team1) {
        firstRoundMatches.push({
          id: `match-${matchCounter}`,
          round: currentRound,
          match_number: matchCounter++,
          team1_id: team1.id,
          team2_id: null,
          team1_score: null,
          team2_score: null,
          winner_id: team1.id, // Automatic win (bye)
          next_match_id: null, // Will set later
          status: 'completed',
        });
      } else if (team1 && team2) {
        firstRoundMatches.push({
          id: `match-${matchCounter}`,
          round: currentRound,
          match_number: matchCounter++,
          team1_id: team1.id,
          team2_id: team2.id,
          team1_score: null,
          team2_score: null,
          winner_id: null,
          next_match_id: null,
          status: 'pending',
        });
      }
    }
    
    matches.push(...firstRoundMatches);
    
    // Generate subsequent rounds
    let previousRoundMatches = firstRoundMatches;
    
    for (let round = 2; round <= totalRounds; round++) {
      const roundMatches: Match[] = [];
      const matchesInRound = Math.pow(2, totalRounds - round);
      
      for (let i = 0; i < matchesInRound; i++) {
        const matchId = `match-${matchCounter}`;
        
        roundMatches.push({
          id: matchId,
          round: round,
          match_number: matchCounter++,
          team1_id: null, // Will be filled by winner of previous match
          team2_id: null,
          team1_score: null,
          team2_score: null,
          winner_id: null,
          next_match_id: null,
          status: 'pending',
        });
        
        // Link previous matches to this match (advancement logic)
        const match1 = previousRoundMatches[i * 2];
        const match2 = previousRoundMatches[i * 2 + 1];
        
        if (match1) match1.next_match_id = matchId;
        if (match2) match2.next_match_id = matchId;
      }
      
      matches.push(...roundMatches);
      previousRoundMatches = roundMatches;
    }
    
    return matches;
  }

  /**
   * Generate double elimination bracket
   * Creates both winner's bracket and loser's bracket
   * 
   * Winner's bracket: standard elimination
   * Loser's bracket: losers get second chance
   * Grand finals: winner's champ vs loser's champ
   */
  static generateDoubleEliminationBracket(teams: Team[]): Match[] {
    // Winner's bracket (standard elimination)
    const winnersBracket = this.generateSingleEliminationBracket(teams);
    
    // Loser's bracket matches
    const losersBracket: Match[] = [];
    let matchCounter = winnersBracket.length + 1;
    
    // For each round in winner's bracket, create loser's bracket matches
    const totalRounds = Math.max(...winnersBracket.map(m => m.round));
    
    for (let round = 1; round < totalRounds; round++) {
      const winnersRoundMatches = winnersBracket.filter(m => m.round === round);
      const losersInRound = winnersRoundMatches.length;
      
      for (let i = 0; i < losersInRound; i++) {
        losersBracket.push({
          id: `loser-match-${matchCounter}`,
          round: round,
          match_number: matchCounter++,
          team1_id: null, // Loser from winner's bracket
          team2_id: null, // Loser from winner's bracket or loser's bracket winner
          team1_score: null,
          team2_score: null,
          winner_id: null,
          next_match_id: null,
          status: 'pending',
        });
      }
    }
    
    // Grand finals (winner's bracket champion vs loser's bracket champion)
    const grandFinals: Match = {
      id: `grand-finals`,
      round: totalRounds + 1,
      match_number: matchCounter++,
      team1_id: null,
      team2_id: null,
      team1_score: null,
      team2_score: null,
      winner_id: null,
      next_match_id: null,
      status: 'pending',
    };
    
    return [...winnersBracket, ...losersBracket, grandFinals];
  }

  /**
   * Advance winner to next match
   * Called when a match is completed
   * 
   * Automatically places winner in next_match_id slot
   * Returns updated matches array
   */
  static advanceWinner(
    matches: Match[],
    completedMatchId: string,
    winnerId: string
  ): Match[] {
    const updatedMatches = [...matches];
    const completedMatch = updatedMatches.find(m => m.id === completedMatchId);
    
    if (!completedMatch || !completedMatch.next_match_id) {
      return updatedMatches; // No advancement needed (finals)
    }
    
    const nextMatch = updatedMatches.find(m => m.id === completedMatch.next_match_id);
    
    if (nextMatch) {
      // Place winner in next match (first available slot)
      if (nextMatch.team1_id === null) {
        nextMatch.team1_id = winnerId;
      } else if (nextMatch.team2_id === null) {
        nextMatch.team2_id = winnerId;
      }
      
      // If both teams are now set, mark match as ready
      if (nextMatch.team1_id && nextMatch.team2_id) {
        nextMatch.status = 'pending';
      }
    }
    
    return updatedMatches;
  }

  // ============================================
  // POOL PLAY GENERATION (Round Robin)
  // ============================================

  /**
   * Generate round robin schedule for a pool
   * Every team plays every other team once
   * 
   * Uses circle method algorithm for balanced scheduling
   */
  static generateRoundRobin(pool: Pool): Match[] {
    const matches: Match[] = [];
    const teams = pool.teams;
    let matchCounter = 1;
    
    // Round robin algorithm
    // If odd number of teams, add a "bye" team
    const teamsList = [...teams];
    if (teamsList.length % 2 !== 0) {
      teamsList.push({ id: 'bye', name: 'BYE', seed: 999 });
    }
    
    const numRounds = teamsList.length - 1;
    const halfSize = teamsList.length / 2;
    
    const teamsIndexed = teamsList.map((team, index) => ({ ...team, index }));
    
    // Generate matches using circle method
    for (let roundNum = 0; roundNum < numRounds; roundNum++) {
      for (let matchNum = 0; matchNum < halfSize; matchNum++) {
        const home = teamsIndexed[matchNum];
        const away = teamsIndexed[teamsList.length - 1 - matchNum];
        
        // Skip if either team is a bye
        if (home.id === 'bye' || away.id === 'bye') {
          continue;
        }
        
        matches.push({
          id: `pool-${pool.id}-match-${matchCounter}`,
          round: roundNum + 1,
          match_number: matchCounter++,
          team1_id: home.id,
          team2_id: away.id,
          team1_score: null,
          team2_score: null,
          winner_id: null,
          next_match_id: null,
          status: 'pending',
          pool_id: pool.id,
        });
      }
      
      // Rotate teams (keep first team fixed)
      teamsIndexed.splice(1, 0, teamsIndexed.pop()!);
    }
    
    return matches;
  }

  /**
   * Distribute teams into pools using snake draft seeding
   * 
   * Example: 12 teams into 3 pools
   * Pool A: seed 1, 6, 7, 12
   * Pool B: seed 2, 5, 8, 11
   * Pool C: seed 3, 4, 9, 10
   * 
   * This ensures balanced pool distribution
   */
  static distributeToPools(teams: Team[], numPools: number): Pool[] {
    const sortedTeams = [...teams].sort((a, b) => (a.seed || 999) - (b.seed || 999));
    
    const pools: Pool[] = [];
    for (let i = 0; i < numPools; i++) {
      pools.push({
        id: `pool-${String.fromCharCode(65 + i)}`, // Pool A, B, C...
        name: `Pool ${String.fromCharCode(65 + i)}`,
        teams: [],
      });
    }
    
    // Snake draft algorithm
    let currentPool = 0;
    let direction = 1; // 1 = forward, -1 = backward
    
    for (const team of sortedTeams) {
      pools[currentPool].teams.push(team);
      
      currentPool += direction;
      
      // Reverse direction at ends (snake pattern)
      if (currentPool >= numPools) {
        currentPool = numPools - 1;
        direction = -1;
      } else if (currentPool < 0) {
        currentPool = 0;
        direction = 1;
      }
    }
    
    return pools;
  }

  /**
   * Calculate pool standings
   * Returns teams sorted by wins, then points differential
   * 
   * Standing format:
   * - wins/losses/ties
   * - points for/against
   * - point differential
   */
  static calculatePoolStandings(pool: Pool, matches: Match[]) {
    const poolMatches = matches.filter(m => m.pool_id === pool.id && m.status === 'completed');
    
    const standings = new Map<string, {
      teamId: string;
      teamName: string;
      wins: number;
      losses: number;
      ties: number;
      pointsFor: number;
      pointsAgainst: number;
      pointsDiff: number;
    }>();
    
    // Initialize standings for all teams
    pool.teams.forEach(team => {
      standings.set(team.id, {
        teamId: team.id,
        teamName: team.name,
        wins: 0,
        losses: 0,
        ties: 0,
        pointsFor: 0,
        pointsAgainst: 0,
        pointsDiff: 0,
      });
    });
    
    // Calculate from completed matches
    poolMatches.forEach(match => {
      const team1Stats = standings.get(match.team1_id!);
      const team2Stats = standings.get(match.team2_id!);
      
      if (!team1Stats || !team2Stats) return;
      
      const score1 = match.team1_score || 0;
      const score2 = match.team2_score || 0;
      
      team1Stats.pointsFor += score1;
      team1Stats.pointsAgainst += score2;
      team2Stats.pointsFor += score2;
      team2Stats.pointsAgainst += score1;
      
      if (score1 > score2) {
        team1Stats.wins++;
        team2Stats.losses++;
      } else if (score2 > score1) {
        team2Stats.wins++;
        team1Stats.losses++;
      } else {
        team1Stats.ties++;
        team2Stats.ties++;
      }
      
      team1Stats.pointsDiff = team1Stats.pointsFor - team1Stats.pointsAgainst;
      team2Stats.pointsDiff = team2Stats.pointsFor - team2Stats.pointsAgainst;
    });
    
    // Sort by wins (desc), then points diff (desc)
    return Array.from(standings.values()).sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      return b.pointsDiff - a.pointsDiff;
    });
  }

  // ============================================
  // SEEDING HELPERS
  // ============================================

  /**
   * Create seeded matchups for fair brackets
   * 1 vs 16, 8 vs 9, 4 vs 13, 5 vs 12, etc.
   * 
   * Standard NCAA tournament seeding pattern
   */
  static createSeededMatchups(teams: Team[]): [Team, Team][] {
    const sorted = [...teams].sort((a, b) => (a.seed || 999) - (b.seed || 999));
    const matchups: [Team, Team][] = [];
    
    const n = sorted.length;
    for (let i = 0; i < n / 2; i++) {
      matchups.push([sorted[i], sorted[n - 1 - i]]);
    }
    
    return matchups;
  }

  /**
   * Validate tournament can be created
   * Checks minimum team requirements
   */
  static validateTournament(teams: Team[], format: BracketType): {
    valid: boolean;
    error?: string;
  } {
    if (teams.length < 2) {
      return { valid: false, error: 'Need at least 2 teams' };
    }
    
    if (format === 'double_elimination' && teams.length < 4) {
      return { valid: false, error: 'Double elimination needs at least 4 teams' };
    }
    
    return { valid: true };
  }

  /**
   * Get bracket depth (number of rounds)
   */
  static getBracketDepth(teamCount: number): number {
    return Math.ceil(Math.log2(teamCount));
  }

  /**
   * Get total match count for bracket
   */
  static getTotalMatches(teamCount: number, format: BracketType): number {
    if (format === 'single_elimination') {
      return teamCount - 1; // Always N-1 matches
    } else {
      // Double elimination: (2N-2) + 1 grand finals
      return (2 * teamCount - 2) + 1;
    }
  }
}

// Export types for use in other modules
export type { Team, Match, Pool, BracketType };
